import { trpc } from "~/utils/trpc";
import { DataTable } from "@quake/shared-ui/components/dataTable/dataTable";
import { Button } from "@quake/shared-ui/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@quake/shared-ui/components/ui/dialog";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "@quake/shared-ui/lib/sonner";
import { validate as validateUUID } from "uuid";
import EditIcon from "lucide-react/dist/esm/icons/edit";
import Trash2 from "lucide-react/dist/esm/icons/trash-2";
import { Permission } from "~/pages/PermissionsFormPage";
import { useBreadCrumbs } from "~/contexts/useBreadCrumbs";

type RoleType = {
  id?: string;
  name: string;
  description?: string;
  permissions: Permission[];
};

const defaultRole = {
  name: "",
  description: "",
  permissions: [],
};

export const Route = createFileRoute("/_dashboard/$datasetSlug/settings/permissions/")({
  component: RouteComponent,
});

function RouteComponent() {
  const { data, status, refetch } = useQuery(trpc.permissions.getRoles.queryOptions());
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const { datasetSlug } = Route.useParams();
  const [currentRole, setCurrentRole] = useState<RoleType>(defaultRole);
  const { data: customModels } = useQuery(trpc.customEntities.getCustomModels.queryOptions());

  const generateName = (resource: string) => {
    return validateUUID(resource) && customModels && customModels.length > 0
      ? customModels.find((item) => item.id === resource)?.name
      : resource;
  };

  const deleteRole = useMutation(
    trpc.permissions.deleteRole.mutationOptions({
      onSuccess: async () => {
        toast.success("Deleted successfully");
        refetch();
      },
      onError: (error) => {
        toast.error(error.message);
      },
      onSettled: () => {
        setCurrentRole(defaultRole);
        setDeleteDialogOpen(false);
      },
    }),
  );

  useEffect(() => {
    refetch();
  }, []);

  useBreadCrumbs([{ title: "Permissions", link: `/${location.pathname.split("/")[1]}/settings/permissions` }]);

  return (
    <div>
      <div className="text-right">
        <Link to="/$datasetSlug/settings/permissions/create" params={{ datasetSlug }}>
          <Button>Add Role</Button>
        </Link>
      </div>
      <DataTable
        mobileLayout="cards"
        initialColumnVisibility={{ Id: false, RoleId: false }}
        data={data ?? []}
        isLoading={status === "pending"}
        showPagination={true}
        showSearchFilter={false}
        showColumnFilter
        manualSorting={true}
        columns={[
          { accessorKey: "name", header: "Name" },
          { accessorKey: "description", header: "Description" },
          {
            accessorKey: "permissions",
            header: "Permissions",
            cell: ({ row }) => {
              return (
                <Link to="/$datasetSlug/settings/permissions/edit/$id" params={{ datasetSlug, id: row.original.id }}>
                  {row.original.id === "admin" ? (
                    <Button className="mr-2" variant={"outline"}>
                      *
                    </Button>
                  ) : (
                    <div>
                      {(() => {
                        const permissions = row.original.permissions || [];
                        const uniqueResources: string[] = [];
                        permissions.forEach((per) => {
                          if (!uniqueResources.includes(per.resource)) {
                            uniqueResources.push(per.resource);
                          }
                        });
                        return (
                          <>
                            <span className="text-xs leading-relaxed md:hidden">
                              {uniqueResources.map(generateName).join(", ")}
                            </span>
                            <div className="hidden md:block">
                              {uniqueResources.map((resource: string) => (
                                <Button key={resource} className="mr-2" variant={"outline"}>
                                  {generateName(resource)}
                                </Button>
                              ))}
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  )}
                </Link>
              );
            },
          },
          {
            header: "Actions",
            cell: ({ row }) => (
              <>
                <div className="flex">
                  {validateUUID(row.original.id) && (
                    <button
                      type="button"
                      aria-label="Delete role"
                      onClick={() => {
                        setCurrentRole({
                          id: row.original.id,
                          name: row.original.name,
                          permissions: [],
                        });
                        setDeleteDialogOpen(true);
                      }}
                      className="mr-2 text-destructive cursor-pointer max-md:flex max-md:size-11 max-md:items-center max-md:justify-center"
                    >
                      <Trash2 size={20} />
                    </button>
                  )}
                  <Link
                    aria-label="Edit role"
                    className="cursor-pointer max-md:min-w-11 max-md:justify-center"
                    to="/$datasetSlug/settings/permissions/edit/$id"
                    params={{ datasetSlug, id: row.original.id }}
                  >
                    <EditIcon size={20} className="mr-2 text-blue-500 cursor-pointer" />
                  </Link>
                </div>
              </>
            ),
          },
        ]}
      />

      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="max-md:w-[calc(100%-1rem)] max-md:rounded-lg max-md:p-4 max-md:[overflow-wrap:anywhere]">
          <DialogHeader className="max-md:pr-6">
            <DialogTitle>Delete Role</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <b>{currentRole.name}</b>? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="max-md:gap-2">
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                if (currentRole.id) {
                  deleteRole.mutate({ id: currentRole.id });
                }
              }}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
